import { useState, useEffect, useCallback } from 'react';
import { Button, Card } from '@etherisc/ui-kit';
import { TrashIcon, BuildingIcon } from 'lucide-react';
import { organizationService } from '../../services/organizationService';
import type { Organization } from '../../types/organization';

interface FarmerOrganizationManagementProps {
  primaryOrganizationId: string;
  additionalOrganizationIds: string[];
  onChange: (organizationIds: string[]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
}

export function FarmerOrganizationManagement({
  primaryOrganizationId,
  additionalOrganizationIds,
  onChange,
  disabled = false,
  errors = {}
}: FarmerOrganizationManagementProps) {
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [primaryOrganization, setPrimaryOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await organizationService.getOrganizations({
          limit: 100, // Get all organizations
          isActive: true
        });

        setAvailableOrganizations(response.organizations);

        // Find the primary organization
        const primary = response.organizations.find(org => org.id === primaryOrganizationId);
        setPrimaryOrganization(primary || null);

      } catch (err) {
        console.error('Error loading organizations:', err);
        setError(err instanceof Error ? err.message : 'Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    if (primaryOrganizationId) {
      loadOrganizations();
    }
  }, [primaryOrganizationId]);

  // Get available organizations for selection (excluding primary and already selected)
  const getAvailableForSelection = useCallback(() => {
    return availableOrganizations.filter(org => 
      org.id !== primaryOrganizationId && 
      !additionalOrganizationIds.includes(org.id)
    );
  }, [availableOrganizations, primaryOrganizationId, additionalOrganizationIds]);

  const handleAddOrganization = useCallback((organizationId: string) => {
    if (!additionalOrganizationIds.includes(organizationId)) {
      onChange([...additionalOrganizationIds, organizationId]);
    }
  }, [additionalOrganizationIds, onChange]);

  const handleRemoveOrganization = useCallback((organizationId: string) => {
    onChange(additionalOrganizationIds.filter(id => id !== organizationId));
  }, [additionalOrganizationIds, onChange]);

  const getOrganizationById = useCallback((id: string) => {
    return availableOrganizations.find(org => org.id === id);
  }, [availableOrganizations]);

  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Organisation Membership</h3>
        <div className="animate-pulse">
          <div className="h-16 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Organisation Membership</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Organisation Membership</h3>
      
      {/* Primary Organisation (Read-only) */}
      {primaryOrganization && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BuildingIcon className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Primary Organisation</p>
                <p className="text-sm text-blue-700">{primaryOrganization.name}</p>
                <p className="text-xs text-blue-600">Inherited from your user account</p>
              </div>
            </div>
            <div className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
              Primary
            </div>
          </div>
        </Card>
      )}

      {/* Additional Organizations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-medium text-gray-900">Additional Organisations</h4>
          {!disabled && getAvailableForSelection().length > 0 && (
            <div className="flex items-center space-x-2">
              <select
                className="text-sm border border-gray-300 rounded-md px-3 py-1"
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddOrganization(e.target.value);
                    e.target.value = ''; // Reset selection
                  }
                }}
                disabled={disabled}
              >
                <option value="">Add organisation...</option>
                {getAvailableForSelection().map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* List of additional organizations */}
        {additionalOrganizationIds.length > 0 ? (
          <div className="space-y-2">
            {additionalOrganizationIds.map(orgId => {
              const org = getOrganizationById(orgId);
              return (
                <Card key={orgId} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <BuildingIcon className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {org?.name || 'Unknown Organisation'}
                        </p>
                        {org?.code && (
                          <p className="text-xs text-gray-500">Code: {org.code}</p>
                        )}
                      </div>
                    </div>
                    {!disabled && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOrganization(orgId)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <BuildingIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No additional organisations assigned</p>
            {!disabled && getAvailableForSelection().length > 0 && (
              <p className="text-xs mt-1">Use the dropdown above to add organisations</p>
            )}
          </div>
        )}
      </div>

      {/* Error display */}
      {errors.organizationIds && (
        <div className="text-sm text-red-600">
          {errors.organizationIds}
        </div>
      )}

      {/* Info text */}
      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
        <p className="font-medium mb-1">Organisation Membership:</p>
        <ul className="space-y-1">
          <li>• The primary organisation is inherited from your user account and cannot be changed</li>
          <li>• Additional organisations can be added to grant access from multiple entities</li>
          <li>• Farmers can be managed by users from any of their assigned organisations</li>
        </ul>
      </div>
    </div>
  );
}